import { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useCases } from "@/hooks/useCases";

type Day = {
  date: string;
  label: string;
  cases: number; // raw row count for that day
  deaths: number;
  casesTotal: number; // cases + deaths (deaths counted as separate cases)
  cumCases: number; // cumulative casesTotal
  cumDeaths: number;
};

function buildDailySeries(cases: { onsetDate?: string; effectiveDate?: string; status: string; deaths?: number }[]): Day[] {
  // Fall back to effectiveDate (reported_date / created_at) so cases without an
  // onset date still appear on the timeline — otherwise the headline drifts
  // below the live tracker totals.
  const items = cases
    .map((c) => ({
      ...c,
      // Normalise to YYYY-MM-DD — created_at comes through as a full ISO
      // timestamp which would never match the day-bucket keys below.
      _date: (c.onsetDate ?? c.effectiveDate)?.slice(0, 10),
    }))
    .filter((c) => c._date);
  const dates = items.map((c) => c._date!);
  if (dates.length === 0) return [];
  const min = new Date(dates.reduce((a, b) => (a < b ? a : b)));
  const latestOnset = new Date(dates.reduce((a, b) => (a > b ? a : b)));
  const today = new Date(new Date().toISOString().slice(0, 10));
  const max = latestOnset > today ? latestOnset : today;
  const days: Day[] = [];
  const cur = new Date(min);
  while (cur <= max) {
    const iso = cur.toISOString().slice(0, 10);
    days.push({
      date: iso,
      label: cur.toLocaleDateString("en", { day: "numeric", month: "short", timeZone: "UTC" }),
      cases: 0,
      deaths: 0,
      casesTotal: 0,
      cumCases: 0,
      cumDeaths: 0,
    });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  const idx = new Map(days.map((d, i) => [d.date, i]));
  for (const c of items) {
    const i = idx.get(c._date!);
    if (i === undefined) continue;
    days[i].cases += 1;
    days[i].deaths += c.deaths ?? 0;
  }
  let cc = 0;
  let cd = 0;
  for (const d of days) {
    d.casesTotal = d.cases + d.deaths;
    cc += d.casesTotal;
    cd += d.deaths;
    d.cumCases = cc;
    d.cumDeaths = cd;
  }
  return days;
}

type Mode = "daily" | "cumulative";
type Metric = "cases" | "deaths";

export default function DailyTrendCharts() {
  const { cases } = useCases();
  // Headline counts must match WHO-reported confirmed cases (deaths included).
  // Suspected/monitoring rows are excluded so the timeline reflects the
  // official outbreak total, not surveillance noise.
  const confirmedCases = useMemo(
    () => cases.filter((c) => c.status === "confirmed"),
    [cases]
  );
  const data = useMemo(() => buildDailySeries(confirmedCases), [confirmedCases]);
  const [mode, setMode] = useState<Mode>("cumulative");
  const [metric, setMetric] = useState<Metric>("cases");

  const totals = useMemo(() => {
    const totalCases = data.reduce((s, d) => s + d.cases, 0);
    const totalDeaths = data.reduce((s, d) => s + d.deaths, 0);
    const peak = data.reduce(
      (acc, d) => (d.casesTotal > acc.cases ? { date: d.label, cases: d.casesTotal } : acc),
      { date: "", cases: 0 }
    );
    return { totalCases, totalDeaths, peak };
  }, [data]);

  if (data.length === 0) return null;

  const isCases = metric === "cases";
  const color = isCases ? "#ef4444" : "#f97316";
  const dataKey =
    mode === "daily"
      ? isCases
        ? "casesTotal"
        : "deaths"
      : isCases
        ? "cumCases"
        : "cumDeaths";
  const headlineValue = isCases ? totals.totalCases + totals.totalDeaths : totals.totalDeaths;

  return (
    <section
      aria-label="Cases daily chart"
      className="border-t border-border bg-background text-foreground"
    >
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-data mb-2">
              Outbreak Timeline
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
              {headlineValue}
              <span className="text-muted-foreground text-lg ml-2 font-normal">
                {isCases ? "cases" : "deaths"} reported
              </span>
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Peak day: <span className="text-foreground font-medium">{totals.peak.date || "—"}</span>
              <span className="mx-2 opacity-30">·</span>
              {data.length} days observed
            </p>
          </div>

          {/* Toggle pills */}
          <div className="flex flex-col gap-2">
            <Toggle
              options={[
                { v: "cases", label: "Cases" },
                { v: "deaths", label: "Deaths" },
              ]}
              value={metric}
              onChange={(v) => setMetric(v as Metric)}
            />
            <Toggle
              options={[
                { v: "daily", label: "Daily" },
                { v: "cumulative", label: "Cumulative" },
              ]}
              value={mode}
              onChange={(v) => setMode(v as Mode)}
            />
          </div>
        </div>

        {/* Chart */}
        <div className="h-[340px] md:h-[400px] -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--border)" strokeDasharray="2 6" vertical={false} opacity={0.5} />
              <XAxis
                dataKey="label"
                tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "var(--border)", opacity: 0.5 }}
                interval="preserveStartEnd"
                minTickGap={28}
                tickMargin={10}
              />
              <YAxis
                tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={32}
              />
              <Tooltip
                cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: "3 3" }}
                content={<FancyTooltip color={color} metric={metric} mode={mode} />}
              />
              {mode === "daily" && totals.peak.cases > 0 && (
                <ReferenceLine
                  y={totals.peak.cases}
                  stroke="var(--muted-foreground)"
                  strokeDasharray="2 4"
                  opacity={0.3}
                />
              )}
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={2.5}
                fill="url(#areaFill)"
                dot={false}
                activeDot={{ r: 6, fill: color, stroke: "var(--background)", strokeWidth: 2 }}
                animationDuration={600}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Footer micro-stats — deaths are counted separately from cases.
            Total = cases + deaths, CFR = deaths / (cases + deaths). */}
        <div className="grid grid-cols-3 gap-3 mt-8 pt-6 border-t border-border">
          <Stat label="Total cases" value={totals.totalCases + totals.totalDeaths} accent="#ef4444" />
          <Stat label="Total deaths" value={totals.totalDeaths} accent="#f97316" />
          <Stat
            label="Case fatality"
            value={
              totals.totalCases + totals.totalDeaths > 0
                ? `${Math.round((totals.totalDeaths / (totals.totalCases + totals.totalDeaths)) * 100)}%`
                : "—"
            }
            accent="var(--muted-foreground)"
          />
        </div>
      </div>
    </section>
  );
}

function Toggle({
  options,
  value,
  onChange,
}: {
  options: { v: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex border border-border rounded-full p-0.5 bg-card/40 self-start">
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`px-3 py-1 text-[11px] uppercase tracking-wider font-data rounded-full transition ${
              active
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent: string }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: accent }}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xl md:text-2xl font-semibold font-data text-foreground mt-0.5">{value}</div>
    </div>
  );
}

function FancyTooltip({
  active,
  payload,
  label,
  color,
  metric,
  mode,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
  color: string;
  metric: Metric;
  mode: Mode;
}) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="bg-card border border-border rounded-md px-3 py-2 shadow-xl backdrop-blur-sm">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-base font-semibold text-foreground font-data">{v}</span>
        <span className="text-xs text-muted-foreground">
          {mode === "cumulative" ? "total" : "new"} {metric}
        </span>
      </div>
    </div>
  );
}
